import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTicketCategories, useTickets, type TicketsFilters } from '@/lib/queries';
import { formatDate } from '@/lib/utils';

const STATUSES = ['open', 'closed', 'archived', 'all'] as const;
type Status = (typeof STATUSES)[number];

export function TicketsListPage() {
  const [status, setStatus] = useState<Status>('open');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [opener, setOpener] = useState<string>('');

  const filters = useMemo<TicketsFilters>(() => {
    const f: TicketsFilters = {};
    if (status !== 'all') f.status = status;
    if (categoryId !== 'all') f.categoryId = categoryId;
    if (opener.trim()) f.opener = opener.trim();
    return f;
  }, [status, categoryId, opener]);

  const tickets = useTickets(filters);
  const cats = useTicketCategories();
  const catNameById = useMemo(
    () => new Map(cats.data?.map((c) => [c.id, c.name]) ?? []),
    [cats.data],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
        <p className="text-muted-foreground">All support tickets across categories</p>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
          <div className="space-y-2">
            <Label id="filter-status-label">Status</Label>
            <div
              className="flex flex-wrap gap-1"
              role="group"
              aria-labelledby="filter-status-label"
            >
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  variant={status === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatus(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="filter-category">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger id="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {cats.data?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.emoji ? `${c.emoji} ` : ''}
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="opener">Opener (Discord ID)</Label>
            <Input
              id="opener"
              value={opener}
              onChange={(e) => setOpener(e.target.value)}
              placeholder="123…"
              inputMode="numeric"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tickets.data?.length ?? 0} ticket(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.data && tickets.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Opener</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead>Closed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.data.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Link to={`/tickets/${t.id}`} className="font-mono text-xs hover:underline">
                        {t.id.slice(0, 8)}…
                      </Link>
                    </TableCell>
                    <TableCell>{catNameById.get(t.categoryId) ?? '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{t.openerDiscordId}</TableCell>
                    <TableCell>
                      <Badge variant={t.status === 'open' ? 'default' : 'secondary'}>
                        {t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(t.openedAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.closedAt ? formatDate(t.closedAt) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tickets.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No tickets match these filters.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
